/**
 * setTableOptionsFormHelpers — runtime helpers + form-values shape for the
 * Slice E3 engine-options form. Extracted from `SetTableOptionsEngineFields.tsx`
 * so the TSX module only exports React components (per the
 * `react-refresh/only-export-components` rule).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E3.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  MssqlDataCompression,
  MssqlDurability,
  MysqlRowFormat,
  OracleLoggingMode,
  TableOptionsChange,
} from '../../../../sql-resolver/types/ddlEdit';

export type SystemVersioningMode = '' | 'on' | 'off';
export type StatsPersistentMode = '' | '0' | '1' | 'default';

export interface TableOptionsFormValues {
  // mssql
  readonly mssqlCompression: '' | MssqlDataCompression;
  readonly mssqlDurability: '' | MssqlDurability;
  readonly mssqlVersioning: SystemVersioningMode;
  readonly mssqlHistoryTable: string;
  // pgsql
  readonly pgsqlSetText: string;
  readonly pgsqlResetText: string;
  // mysql
  readonly mysqlEngine: string;
  readonly mysqlCharset: string;
  readonly mysqlCollate: string;
  readonly mysqlRowFormat: '' | MysqlRowFormat;
  readonly mysqlKeyBlockSize: string;
  readonly mysqlStatsPersistent: StatsPersistentMode;
  // oracle
  readonly oraclePctfree: string;
  readonly oraclePctused: string;
  readonly oracleInitrans: string;
  readonly oracleMaxtrans: string;
  readonly oracleStorage: string;
  readonly oracleLogging: '' | OracleLoggingMode;
  readonly oracleCompress: string;
}

export const DEFAULT_TABLE_OPTIONS_FORM_VALUES: TableOptionsFormValues = {
  mssqlCompression: '',
  mssqlDurability: '',
  mssqlVersioning: '',
  mssqlHistoryTable: '',
  pgsqlSetText: '',
  pgsqlResetText: '',
  mysqlEngine: '',
  mysqlCharset: '',
  mysqlCollate: '',
  mysqlRowFormat: '',
  mysqlKeyBlockSize: '',
  mysqlStatsPersistent: '',
  oraclePctfree: '',
  oraclePctused: '',
  oracleInitrans: '',
  oracleMaxtrans: '',
  oracleStorage: '',
  oracleLogging: '',
  oracleCompress: '',
};

function parseLineList(input: string): string[] {
  return input.split(/\r?\n/).map((s) => s.trim()).filter((s) => s.length > 0);
}

function parseOptionalNumber(text: string): number | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : NaN;
}

type MutableTableOptionsChange = {
  -readonly [K in keyof TableOptionsChange]: TableOptionsChange[K];
};

export function buildTableOptionsChange(
  engine: SqlEngine,
  v: TableOptionsFormValues,
): { readonly change: TableOptionsChange } | { readonly error: string } {
  if (engine === 'mssql') {
    const change: MutableTableOptionsChange = {};
    if (v.mssqlCompression !== '') change.mssqlDataCompression = v.mssqlCompression;
    if (v.mssqlDurability !== '') change.mssqlDurability = v.mssqlDurability;
    if (v.mssqlVersioning === 'off') change.mssqlSystemVersioning = { enabled: false };
    else if (v.mssqlVersioning === 'on') {
      const ht = v.mssqlHistoryTable.trim();
      change.mssqlSystemVersioning = ht.length > 0
        ? { enabled: true, historyTable: ht }
        : { enabled: true };
    }
    return { change };
  }
  if (engine === 'postgres') {
    const change: MutableTableOptionsChange = {};
    const setEntries = parseLineList(v.pgsqlSetText);
    const resetEntries = parseLineList(v.pgsqlResetText);
    if (setEntries.length > 0) change.pgsqlSetOptions = setEntries;
    if (resetEntries.length > 0) change.pgsqlResetOptions = resetEntries;
    return { change };
  }
  if (engine === 'mysql') {
    const change: MutableTableOptionsChange = {};
    if (v.mysqlEngine.trim() !== '') change.mysqlEngine = v.mysqlEngine.trim();
    if (v.mysqlCharset.trim() !== '') change.mysqlDefaultCharset = v.mysqlCharset.trim();
    if (v.mysqlCollate.trim() !== '') change.mysqlCollate = v.mysqlCollate.trim();
    if (v.mysqlRowFormat !== '') change.mysqlRowFormat = v.mysqlRowFormat;
    const kbs = parseOptionalNumber(v.mysqlKeyBlockSize);
    if (kbs !== undefined) {
      if (Number.isNaN(kbs)) return { error: 'KEY_BLOCK_SIZE must be a number.' };
      change.mysqlKeyBlockSize = kbs;
    }
    if (v.mysqlStatsPersistent !== '') {
      change.mysqlStatsPersistent = v.mysqlStatsPersistent === 'default'
        ? 'default'
        : (v.mysqlStatsPersistent === '1' ? 1 : 0);
    }
    return { change };
  }
  // oracle
  const change: MutableTableOptionsChange = {};
  for (const [label, key, raw] of [
    ['PCTFREE', 'oraclePctfree', v.oraclePctfree],
    ['PCTUSED', 'oraclePctused', v.oraclePctused],
    ['INITRANS', 'oracleInitrans', v.oracleInitrans],
    ['MAXTRANS', 'oracleMaxtrans', v.oracleMaxtrans],
  ] as const) {
    const value = parseOptionalNumber(raw);
    if (value === undefined) continue;
    if (Number.isNaN(value)) return { error: `${label} must be a number.` };
    change[key] = value;
  }
  if (v.oracleStorage.trim() !== '') change.oracleStorageText = v.oracleStorage.trim();
  if (v.oracleLogging !== '') change.oracleLogging = v.oracleLogging;
  if (v.oracleCompress.trim() !== '') change.oracleCompressText = v.oracleCompress.trim();
  return { change };
}
